import { DigitalFormSignatureStatusEnum } from 'src/app/Enums/DigitalFormEnum';
import { ProcessStatusEnum } from 'src/app/Enums/ProcessStatusEnum';
import { RequestStatusEnum } from 'src/app/Enums/RequestStatusEnum';

/**
 * Como a situação de uma solicitação aparece para o cidadão. Ele não precisa saber
 * que há dois status por trás (o da solicitação e o do processo que ela gera): vê
 * um rótulo em linguagem simples, a cor do que isso significa para ele e uma frase
 * dizendo o que acontece agora.
 */

export type RqTone = 'info' | 'success' | 'warning' | 'danger' | 'signature' | 'teal' | 'neutral';

/** Ícone de cada área da Prefeitura: ajuda a reconhecer o serviço sem ler o órgão. */
const ORG_ICONS: Array<[RegExp, string]> = [
    [/SA[ÚU]DE/, 'ph-first-aid-kit'],
    [/EDUCA/, 'ph-graduation-cap'],
    [/FINAN[ÇC]/, 'ph-coins'],
    [/ADMINISTRA/, 'ph-briefcase'],
    [/MEIO AMBIENTE|URBANISMO/, 'ph-tree'],
    [/PROCURADORIA/, 'ph-scales'],
    [/INFRAESTRUTURA|OBRAS/, 'ph-hard-hat'],
    [/ASSIST[ÊE]NCIA|SOCIAL|TRABALHO/, 'ph-hand-heart'],
    [/OUVIDORIA/, 'ph-megaphone'],
    [/CONTROLADORIA/, 'ph-shield-check'],
    [/CULTURA|TURISMO/, 'ph-mask-happy'],
    [/ESPORTE/, 'ph-soccer-ball'],
    [/AGRICULTURA/, 'ph-plant'],
    [/GOVERNO|GABINETE|PREFEIT/, 'ph-bank'],
];

export function orgIcon(descricao: string | null | undefined): string {
    const nome = String(descricao || '').toUpperCase();
    const encontrado = ORG_ICONS.find(([padrao]) => padrao.test(nome));

    return 'ph ' + (encontrado ? encontrado[1] : 'ph-buildings');
}

export interface IRqStatus {
    label: string;
    tone: RqTone;
    description: string;
    /** Ainda em movimento (análise, assinatura): o ponto do status pulsa. */
    live?: boolean;
}

const REQUEST_STATUS: Record<number, IRqStatus> = {
    [RequestStatusEnum.evaluation]: {
        label: 'Em análise',
        tone: 'info',
        live: true,
        description: 'A triagem está conferindo seus dados e documentos.',
    },
    [RequestStatusEnum.accepted]: {
        label: 'Aceita',
        tone: 'success',
        description: 'A solicitação virou processo e segue no setor responsável.',
    },
    [RequestStatusEnum.correction]: {
        label: 'Precisa de correção',
        tone: 'warning',
        description: 'Faltou algo. Veja o que ajustar e reenvie dentro do prazo.',
    },
    [RequestStatusEnum.canceled]: {
        label: 'Anulada',
        tone: 'danger',
        description: 'A solicitação foi anulada. Veja o motivo no detalhe.',
    },
    [RequestStatusEnum.corrected]: {
        label: 'Correção enviada',
        tone: 'teal',
        live: true,
        description: 'Recebemos sua correção. A triagem vai conferir de novo.',
    },
    [RequestStatusEnum.outdated]: {
        label: 'Vencida',
        tone: 'neutral',
        description: 'O prazo da solicitação terminou.',
    },
    [RequestStatusEnum.awaitingSignature]: {
        label: 'Aguardando assinatura',
        tone: 'signature',
        live: true,
        description: 'Aprovada na triagem. Falta a assinatura do responsável pelo órgão para virar processo.',
    },
};

const PROCESS_STATUS: Record<number, IRqStatus> = {
    [ProcessStatusEnum.NEW]: {
        label: 'Processo aberto',
        tone: 'info',
        description: 'O processo foi aberto e está chegando ao setor responsável.',
    },
    [ProcessStatusEnum.IN_PROGRESS]: {
        label: 'Em andamento',
        tone: 'info',
        live: true,
        description: 'O setor responsável está analisando o processo.',
    },
    [ProcessStatusEnum.ARCHIVED]: {
        label: 'Arquivado',
        tone: 'neutral',
        description: 'O processo foi finalizado e arquivado.',
    },
    [ProcessStatusEnum.CONCLUDED]: {
        label: 'Concluído',
        tone: 'success',
        description: 'O processo foi concluído. Veja a resposta no detalhe.',
    },
    [ProcessStatusEnum.IN_CORRECTION]: {
        label: 'Precisa de correção',
        tone: 'warning',
        description: 'O setor pediu um ajuste. Abra o processo para responder.',
    },
    [ProcessStatusEnum.OUTDATED]: {
        label: 'Vencido',
        tone: 'neutral',
        description: 'O prazo do processo terminou.',
    },
    [ProcessStatusEnum.CANCELED]: {
        label: 'Anulado',
        tone: 'danger',
        description: 'O processo foi anulado. Veja o motivo no detalhe.',
    },
    [ProcessStatusEnum.ANSWERED]: {
        label: 'Resposta enviada',
        tone: 'teal',
        live: true,
        description: 'Você respondeu ao setor. Agora é com eles.',
    },
    [ProcessStatusEnum.EXPIRED_WITHOUT_ANALYSIS]: {
        label: 'Vencido sem análise',
        tone: 'neutral',
        description: 'O prazo terminou antes da análise.',
    },
    [ProcessStatusEnum.AWAITING_REFUND]: {
        label: 'Aguardando restituição',
        tone: 'info',
        description: 'A restituição está sendo providenciada.',
    },
    [ProcessStatusEnum.PENDING_WITHOUT_RESPONSIBLE]: {
        label: 'Na fila do setor',
        tone: 'info',
        description: 'O processo chegou ao setor e aguarda um responsável.',
    },
    [ProcessStatusEnum.AWAITING_PAYMENT]: {
        label: 'Aguardando pagamento',
        tone: 'warning',
        description: 'Há um pagamento pendente. Abra o processo para ver a guia.',
    },
    [ProcessStatusEnum.APENSADO]: {
        label: 'Apensado',
        tone: 'neutral',
        description: 'O processo foi juntado a outro e segue com ele.',
    },
    [ProcessStatusEnum.AWAITING_SECRETARY_SIGNATURE]: {
        label: 'Aguardando assinatura',
        tone: 'signature',
        live: true,
        description: 'A certidão aguarda a assinatura do(a) Secretário(a) e fica disponível logo depois.',
    },
};

const UNKNOWN: IRqStatus = { label: 'Em andamento', tone: 'neutral', description: '' };

/** O requerimento digital da solicitação está parado na assinatura do responsável. */
export function isAwaitingResponsibleSignature(item: any): boolean {
    return (
        item?.SOL_Status === RequestStatusEnum.awaitingSignature ||
        (item?.statusAssinaturaRequerimento === DigitalFormSignatureStatusEnum.AGUARDANDO_RESPONSAVEL &&
            item?.SOL_Status !== RequestStatusEnum.canceled)
    );
}

export function requestStatus(item: any): IRqStatus {
    if (isAwaitingResponsibleSignature(item)) return REQUEST_STATUS[RequestStatusEnum.awaitingSignature];

    return REQUEST_STATUS[item?.SOL_Status] || UNKNOWN;
}

export function processStatus(status: number): IRqStatus {
    return PROCESS_STATUS[status] || UNKNOWN;
}

/** Situação que o cidadão vê numa linha da lista: a do processo, quando já existe. */
export function citizenStatus(item: any): IRqStatus {
    return item?.PRC_NumeroProcesso ? processStatus(item.ProcessoStatus) : requestStatus(item);
}

// ----------------------------------------------------------------------
// Etapas: Enviada → Análise → Processo → Concluída
// ----------------------------------------------------------------------

export const REQUEST_STAGES = ['Enviada', 'Análise', 'Processo', 'Concluída'];

export interface IRqStage {
    /** Índice em REQUEST_STAGES da etapa em que a solicitação está. */
    current: number;
    /** A caminhada parou aí (anulada ou vencida). */
    stopped: boolean;
    tone: RqTone;
}

const REQUEST_STOPPED = [RequestStatusEnum.canceled, RequestStatusEnum.outdated];
const PROCESS_STOPPED = [ProcessStatusEnum.CANCELED, ProcessStatusEnum.OUTDATED, ProcessStatusEnum.EXPIRED_WITHOUT_ANALYSIS];
const PROCESS_CONCLUDED = [ProcessStatusEnum.CONCLUDED, ProcessStatusEnum.ARCHIVED];

export function requestStage(item: any): IRqStage {
    const tone = citizenStatus(item).tone;

    if (!item?.PRC_NumeroProcesso) {
        return { current: 1, stopped: REQUEST_STOPPED.includes(item?.SOL_Status), tone };
    }

    if (PROCESS_CONCLUDED.includes(item.ProcessoStatus)) {
        return { current: 3, stopped: false, tone };
    }

    return { current: 2, stopped: PROCESS_STOPPED.includes(item.ProcessoStatus), tone };
}

// ----------------------------------------------------------------------
// Grupos de "Minhas solicitações". Espelham RequestBucketEnum da API, que conta
// quantas há em cada um; aqui viram a condição da listagem de cada aba.
// ----------------------------------------------------------------------

export enum RequestBucketEnum {
    ACAO = 'acao',
    ANDAMENTO = 'andamento',
    CONCLUIDAS = 'concluidas',
    ENCERRADAS = 'encerradas',
}

export const REQUEST_BUCKET_CONDITIONS: Record<RequestBucketEnum, string> = {
    [RequestBucketEnum.ACAO]: `("SOL_Status" = 2 OR "ProcessoStatus" = 5)`,
    [RequestBucketEnum.ANDAMENTO]: `("SOL_Status" IN (0,4,6) OR ("SOL_Status" = 1 AND ("ProcessoStatus" IS NULL OR "ProcessoStatus" NOT IN (3,4,5,6,7,9))))`,
    [RequestBucketEnum.CONCLUIDAS]: `("SOL_Status" = 1 AND "ProcessoStatus" IN (3,4))`,
    [RequestBucketEnum.ENCERRADAS]: `("SOL_Status" IN (3,5) OR ("SOL_Status" = 1 AND "ProcessoStatus" IN (6,7,9)))`,
};
